import React, { useEffect, useRef, useState } from 'react';

import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Badge } from '@/components/ui/badge';
import { Card as ShadcnCard, CardContent } from '@/components/ui/card';
import { Avatar } from '../../../../../../../shared/components/Avatar';
import { Tag } from '../../../../../../shared/components/Tag';
import { TASK_PRIORITIES } from '../../../../constants';
import { taskShape } from '../../../../types';
import { formatDate } from '../../../../utils';
import DropdownMenu from './DropdownMenu';

const CARD_STYLES =
  'bg-white border-0 mb-2 py-0 rounded-sm transition-colors hover:bg-surface-light';

const TaskCard = ({ onDeleteClick, onEditClick, statusId, task }) => {
  const ref = useRef(null);

  const [dragging, setDragging] = useState(false);

  const { color: priorityColor, icon: priorityIcon } =
    TASK_PRIORITIES[task.priority || 'none'];

  useEffect(() => {
    const element = ref.current;

    const handleDrop = (element) => {
      setDragging(false);
      triggerPostMoveFlash(element);
    };

    return draggable({
      element,
      getInitialData: () => ({ id: task.id, statusId, type: 'task' }),
      onDragStart: () => setDragging(true),
      onDrop: () => handleDrop(element),
    });
  }, [task.id, statusId]);

  return (
    <ShadcnCard
      className={CARD_STYLES}
      ref={ref}
      style={dragging ? { opacity: 0.4 } : {}}
    >
      <CardContent className="p-3">
        <div className="flex items-center mb-1 gap-2">
          <span className="font-medium text-foreground">{task.name}</span>

          {!isEmpty(task.caseFile?.name) && (
            <Badge className="border-border bg-surface text-muted-foreground">
              {task.caseFile.name}
            </Badge>
          )}

          <div className="ml-auto">
            <DropdownMenu
              onDeleteClick={onDeleteClick}
              onEditClick={onEditClick}
              taskId={task.id}
            />
          </div>
        </div>

        <div className="mb-2 flex items-center gap-1.5">
          <FontAwesomeIcon className={priorityColor} icon={priorityIcon} />

          {task.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {task.tags.map((tag) => (
                <Tag color={tag.color} key={tag.id} name={tag.name} />
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Created {formatDate(task.createdAt)}
          </span>

          {task.assignee && (
            <Avatar
              backgroundColor={task.assignee.avatarColor}
              initials={task.assignee.initials}
              size="sm"
            />
          )}
        </div>
      </CardContent>
    </ShadcnCard>
  );
};

TaskCard.propTypes = {
  onDeleteClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  statusId: PropTypes.string.isRequired,
  task: taskShape.isRequired,
};

export default TaskCard;
